// <reviews-marquee-live> — fills the "Reviews marquee" section with the product's reviews
// from Judge.me (the same ones the Judge.me widget shows), as two rows of cards that drift
// sideways in an endless loop. Nothing is written into the theme: edit or delete reviews in
// Judge.me and this follows. If the reviews can't be loaded, the section stays hidden.

if (!customElements.get('reviews-marquee-live')) {
  const MIN_TEXT = 40;
  const MAX_TEXT = 280;
  const SECOND_ROW_SPEED = 0.7;

  const textFromHtml = (html) => new DOMParser().parseFromString(html || '', 'text/html').body.textContent.trim();

  class ReviewsMarqueeLive extends HTMLElement {
    async connectedCallback() {
      const { shop, productId } = this.dataset;
      const root = this.closest('.reviews-marquee');
      if (!shop || !productId || !root) return;

      // The strip sits far down the page, so don't fetch or build it until the shopper is close.
      await this.#whenNearViewport(root);

      try {
        const reviews = await this.#fetchReviews(shop, productId);
        if (reviews.length === 0) return;

        this.#render(reviews);
        // Unhide before measuring: a hidden section has no layout, so no widths.
        root.hidden = false;
        this.#setDurations();

        window.matchMedia('(max-width: 749px)').addEventListener('change', () => {
          // Card widths change with the breakpoint, so measure after the new layout.
          requestAnimationFrame(() => this.#setDurations());
        });
      } catch (error) {
        console.warn('[reviews-marquee] Could not load reviews:', error);
      }
    }

    /**
     * Resolves once the section is within ~800px of the screen. The section is `hidden` until it
     * has content (a hidden element has no box to observe), so its wrapper is observed instead:
     * that wrapper sits exactly where the section will appear.
     */
    #whenNearViewport(root) {
      const target = root.parentElement || root;
      if (!('IntersectionObserver' in window)) return Promise.resolve();

      return new Promise((resolve) => {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            resolve();
          },
          { rootMargin: '800px 0px' }
        );
        observer.observe(target);
      });
    }

    async #fetchReviews(shop, productId) {
      const max = Number(this.dataset.max) || 24;
      const params = new URLSearchParams({
        url: shop,
        shop_domain: shop,
        platform: 'shopify',
        product_id: productId,
        per_page: '30',
        page: '1',
      });

      const response = await fetch(`https://judge.me/reviews/reviews_for_widget?${params}`, { credentials: 'omit' });
      if (!response.ok) throw new Error(`Judge.me responded ${response.status}`);
      const data = await response.json();

      // Only reviews that fit a card nicely; ratings are not filtered, so the mix matches Judge.me.
      return (data.reviews || [])
        .map((review) => ({
          name: review.reviewer_name,
          rating: Math.min(5, Math.max(1, Number(review.rating) || 5)),
          title: review.title || '',
          text: textFromHtml(review.body_html),
          verified: Boolean(review.verified_buyer),
        }))
        .filter((review) => review.name && review.text.length >= MIN_TEXT && review.text.length <= MAX_TEXT)
        .slice(0, max);
    }

    #card(review) {
      const card = document.createElement('li');
      card.className = 'reviews-marquee__card';
      // Clicking a card opens the reviews popup (see below).
      card.dataset.rmOpen = '';
      card.tabIndex = 0;
      card.setAttribute('aria-haspopup', 'dialog');

      const stars = document.createElement('div');
      stars.className = 'reviews-marquee__stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      for (let star = 1; star <= 5; star++) {
        const span = document.createElement('span');
        span.className = 'reviews-marquee__star' + (star <= review.rating ? ' is-on' : '');
        span.setAttribute('aria-hidden', 'true');
        span.textContent = '★';
        stars.append(span);
      }
      card.append(stars);

      if (review.title) {
        const title = document.createElement('p');
        title.className = 'reviews-marquee__title';
        title.textContent = review.title;
        card.append(title);
      }

      const text = document.createElement('p');
      text.className = 'reviews-marquee__text';
      text.textContent = review.text;
      card.append(text);

      const meta = document.createElement('p');
      meta.className = 'reviews-marquee__meta';
      const name = document.createElement('span');
      name.className = 'reviews-marquee__name';
      name.textContent = review.name;
      meta.append(name);
      if (review.verified) {
        const verified = document.createElement('span');
        verified.className = 'reviews-marquee__verified';
        verified.textContent = 'Verified buyer';
        meta.append(verified);
      }
      card.append(meta);

      return card;
    }

    #render(reviews) {
      const rowCount = this.dataset.rows === '1' || reviews.length < 4 ? 1 : 2;
      const rows = Array.from({ length: rowCount }, () => []);
      reviews.forEach((review, index) => rows[index % rowCount].push(review));

      this.replaceChildren();

      rows.forEach((rowReviews) => {
        const row = document.createElement('div');
        row.className = 'reviews-marquee__row';

        // Repeat the cards so one strip is wider than any screen, then render the strip twice:
        // the animation slides by one strip's width, which lands exactly on the second copy.
        const repeats = Math.floor(12 / rowReviews.length) + 1;
        for (let copy = 1; copy <= 2; copy++) {
          const strip = document.createElement('ul');
          strip.className = 'reviews-marquee__strip';
          strip.setAttribute('role', 'list');
          if (copy === 2) {
            strip.setAttribute('aria-hidden', 'true');
            strip.setAttribute('inert', '');
          }
          for (let repeat = 0; repeat < repeats; repeat++) {
            rowReviews.forEach((review) => strip.append(this.#card(review)));
          }
          row.append(strip);
        }

        this.append(row);
      });
    }

    // Set once the cards are laid out, so the speed is a true px/second. Phones get their own
    // (slower) speed; it is re-applied when the screen crosses the phone breakpoint.
    #setDurations() {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const speed = Number(mobile ? this.dataset.speedMobile : this.dataset.speed) || 75;
      const gap = parseFloat(getComputedStyle(this).getPropertyValue('--rm-gap')) || 16;

      this.querySelectorAll('.reviews-marquee__row').forEach((row, rowIndex) => {
        const strip = row.querySelector('.reviews-marquee__strip');
        const rowSpeed = rowIndex === 1 ? speed * SECOND_ROW_SPEED : speed;
        const duration = Math.max(10, Math.round((strip.scrollWidth + gap) / rowSpeed));
        row.style.setProperty('--rm-duration', `${duration}s`);
        row.style.setProperty('--rm-delay', rowIndex === 1 ? `${-Math.round(duration / 3)}s` : '0s');
      });
    }
  }

  customElements.define('reviews-marquee-live', ReviewsMarqueeLive);

  // ---- Reviews popup -------------------------------------------------------------------
  // The product template keeps the normal Judge.me review widget in a section with the key
  // "reviews_popup_pb", parked off-screen by the section's CSS. Clicking a review (or one of the
  // buttons) shows that section as a full-screen overlay, so the shopper gets Judge.me's own list
  // with sorting, filters and the write-a-review form. The moving strip itself has no controls.
  // Section ids are prefixed with the template ("shopify-section-template--123__reviews_popup_pb").
  const POPUP_SELECTOR = ".shopify-section[id$='__reviews_popup_pb']";
  const OPEN_CLASS = 'reviews-popup--open';
  let lastFocus = null;

  // Judge.me's write-a-review form has its "Review title" field switched off in the Judge.me
  // admin (and titles are auto-made from the text instead). Turn it on here, so shoppers can add
  // a title; the form reads these settings each time it opens.
  const enableReviewTitle = () => {
    if (window.jdgmSettings) {
      window.jdgmSettings.widget_show_review_title_input = true;
      window.jdgmSettings.widget_autofill_review_title = false;
    }
  };
  enableReviewTitle();

  const popupSection = () => document.querySelector(POPUP_SELECTOR);
  const isOpen = () => popupSection()?.classList.contains(OPEN_CLASS) ?? false;

  /** Parks the widget (inert while closed), adds the close button and reveals the page buttons. */
  function preparePopup() {
    const section = popupSection();
    if (!section) return false;

    if (!section.dataset.rmReady) {
      section.dataset.rmReady = 'true';
      section.setAttribute('inert', '');
      section.setAttribute('aria-hidden', 'true');
      section.setAttribute('role', 'dialog');
      section.setAttribute('aria-label', 'Customer reviews');

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'reviews-popup__close';
      close.setAttribute('aria-label', 'Close reviews');
      close.textContent = '×';
      close.addEventListener('click', closeReviews);
      section.prepend(close);

      // Clicking the dark backdrop (not the widget itself) closes it.
      section.addEventListener('click', (event) => {
        if (event.target === section) closeReviews();
      });
    }

    document.querySelectorAll('[data-rm-actions]').forEach((actions) => {
      actions.hidden = false;
    });
    return true;
  }

  /**
   * Judge.me loads its widget and its (many) style files after the page. If the popup is opened
   * before they are in, the header and filters show up as raw, unstyled markup. So the content is
   * kept hidden behind a short "Loading reviews…" until the widget is rendered and its layout
   * styles are applied (or a few seconds pass, so a blocked file can never leave it blank).
   */
  function waitForWidgetStyles(section) {
    const ready = () => {
      if (!section.querySelector('.jm-review-item, .jdgm-rev')) return false;
      // .jm-cluster is `display: flex` once Judge.me's layout styles are in (.jm-stack is a plain block).
      const cluster = section.querySelector('.jm-cluster');
      return !cluster || getComputedStyle(cluster).display === 'flex';
    };

    if (ready()) return;

    section.classList.add('reviews-popup--loading');
    let tries = 0;
    const timer = setInterval(() => {
      tries++;
      if (ready() || tries > 80 || !isOpen()) {
        clearInterval(timer);
        section.classList.remove('reviews-popup--loading');
      }
    }, 100);
  }

  /** Clicks Judge.me's own "Write a review" button once the widget has rendered it. */
  function clickWriteButton(section) {
    const label = (window.jdgmSettings && window.jdgmSettings.widget_open_form_text) || 'Write a review';
    const wanted = new RegExp(label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
    const fallback = /write\s+a\s+review/i;
    let tries = 0;

    const timer = setInterval(() => {
      tries++;
      const button = Array.from(section.querySelectorAll('button, a, [role="button"]')).find((el) => {
        const text = el.textContent.trim();
        return wanted.test(text) || fallback.test(text);
      });
      if (button) {
        clearInterval(timer);
        button.click();
      } else if (tries > 60) {
        clearInterval(timer);
      }
    }, 200);
  }

  function openReviews({ write = false } = {}) {
    if (!preparePopup()) return;
    const section = popupSection();

    // "Write a review" goes straight to Judge.me's form (it opens over the page itself),
    // without showing the list of reviews first.
    if (write) {
      enableReviewTitle();
      clickWriteButton(section);
      return;
    }

    lastFocus = document.activeElement;
    section.removeAttribute('inert');
    section.removeAttribute('aria-hidden');
    section.setAttribute('aria-modal', 'true');
    section.classList.add(OPEN_CLASS);
    section.scrollTop = 0;
    waitForWidgetStyles(section);
    // The theme's own scroll lock (base.css) works for both the window and .page-wrapper.
    document.documentElement.setAttribute('scroll-lock', '');

    section.querySelector('.reviews-popup__close')?.focus({ preventScroll: true });
  }

  function closeReviews() {
    const section = popupSection();
    if (!section || !isOpen()) return;

    section.classList.remove(OPEN_CLASS);
    section.setAttribute('inert', '');
    section.setAttribute('aria-hidden', 'true');
    section.removeAttribute('aria-modal');
    document.documentElement.removeAttribute('scroll-lock');
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  }

  document.addEventListener('click', (event) => {
    const action = event.target.closest('[data-rm-action]');
    if (action) {
      event.preventDefault();
      openReviews({ write: action.dataset.rmAction === 'write' });
      return;
    }

    const card = event.target.closest('[data-rm-open]');
    if (card) openReviews();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen()) {
      closeReviews();
      return;
    }

    if ((event.key === 'Enter' || event.key === ' ') && event.target.matches?.('[data-rm-open]')) {
      event.preventDefault();
      openReviews();
    }
  });

  preparePopup();
  document.addEventListener('shopify:section:load', preparePopup);
}
